// Hand-maintained — override generation
import {
  deleteAdminUsersById,
  getAdminUsers,
  getAdminUsersById,
  getAdminUsersByEmail,
  patchAdminUsersById,
  patchAdminUsersByIdAdmin,
  patchAdminUsersByIdAdminEmail,
  patchAdminUsersByIdConfirmEmail,
  patchAdminUsersByIdResetPassword,
  postAdminUsersAuthRegisterViaInvitation,
} from "../_internal/sdk.gen";
import type { User } from "../_internal/types.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";
import { paginateToArray } from "../pagination";
import { buildPageQuery } from "../namespace-types";
import type { JsonApiQueryWithPage } from "../namespace-types";

/** Attributes for admin-level user updates (platform admin flags, password reset). */
export type AdminUpdateUserAttributes = {
  is_platform_admin?: boolean;
  password?: string;
  password_confirmation?: string;
};

/**
 * Attributes accepted when a user updates their own preferences via PATCH /admin/users/:id.
 * Mirrors the `:update` action's `accept` list. Self-scoped — non-admin callers
 * can only update their own record.
 */
export type UpdateUserAttributes = {
  theme_preference?: string | null;
};

export function createUsersNamespace(rb: RequestBuilder) {
  return {
    /**
     * List all users (paginated).
     * @param options - Optional page and pageSize along with request options
     * @returns Array of users
     * @example
     * ```typescript
     * const users = await admin.users.list({ page: 1, pageSize: 50 });
     * ```
     */
    list: async (
      options?: { page?: number; pageSize?: number } & RequestOptions,
    ): Promise<User[]> => {
      return rb.execute<User[]>(
        getAdminUsers,
        buildPageQuery(options?.page, options?.pageSize),
        options,
      );
    },

    /**
     * List all users (fetches all pages automatically).
     * @param options - Optional request options.
     * @returns Complete array of all users
     * @example
     * ```typescript
     * const users = await admin.users.listAll();
     * ```
     */
    listAll: async (options?: RequestOptions): Promise<User[]> => {
      return paginateToArray(
        rb.createPaginatedFetcher<User>(
          getAdminUsers,
          (page, pageSize): JsonApiQueryWithPage => ({
            query: { page: { number: page, size: pageSize } },
          }),
          options,
        ),
      );
    },

    /**
     * Get a user by ID.
     * @param id - User ID
     * @returns User object
     * @example
     * ```typescript
     * const user = await admin.users.get("user_123");
     * ```
     */
    get: async (id: string, options?: RequestOptions): Promise<User> => {
      return rb.execute<User>(getAdminUsersById, { path: { id } }, options);
    },

    /**
     * Look up a user by email address.
     * @param email - Email address to search
     * @returns User object
     * @example
     * ```typescript
     * const user = await admin.users.getByEmail("ada@example.com");
     * ```
     */
    getByEmail: async (
      email: string,
      options?: RequestOptions,
    ): Promise<User> => {
      return rb.execute<User>(
        getAdminUsersByEmail,
        { query: { email } },
        options,
      );
    },

    /**
     * Update a user's own preferences (currently `theme_preference`).
     * Self-scoped via policy: non-admin callers can only update their own record.
     * @param id - User ID
     * @param attributes - User preference attributes to update
     * @returns Updated user
     * @example
     * ```typescript
     * await admin.users.update("user_123", { theme_preference: "dark" });
     * ```
     */
    update: async (
      id: string,
      attributes: UpdateUserAttributes,
      options?: RequestOptions,
    ): Promise<User> => {
      const { theme_preference } = attributes;
      return rb.execute<User>(
        patchAdminUsersById,
        {
          path: { id },
          body: {
            data: { id, type: "user", attributes: { theme_preference } },
          },
        },
        options,
      );
    },

    /**
     * Update a user's admin-level attributes (is_platform_admin, is_app_admin).
     * @param id - User ID
     * @param attributes - Admin attributes to update
     * @returns Updated user
     * @example
     * ```typescript
     * await admin.users.adminUpdate("user_123", { is_platform_admin: true });
     * ```
     */
    adminUpdate: async (
      id: string,
      attributes: AdminUpdateUserAttributes,
      options?: RequestOptions,
    ): Promise<User> => {
      const { is_platform_admin, password, password_confirmation } = attributes;

      return rb.execute<User>(
        patchAdminUsersByIdAdmin,
        {
          path: { id },
          body: {
            data: {
              id,
              type: "user",
              attributes: {
                is_platform_admin,
                password,
                password_confirmation,
              },
            },
          },
        },
        options,
      );
    },

    /**
     * Change a user's email address (admin action).
     * @param id - User ID
     * @param email - New email address
     * @returns Updated user
     * @example
     * ```typescript
     * await admin.users.adminUpdateEmail("user_123", "new@example.com");
     * ```
     */
    adminUpdateEmail: async (
      id: string,
      email: string,
      options?: RequestOptions,
    ): Promise<User> => {
      // Server action `admin_update_email` declares `argument :new_email`,
      // so the request body must send `new_email` under attributes (not
      // `email`). Verified against sdks/oas/openapi_base.json — schema
      // requires data.attributes.new_email.
      return rb.execute<User>(
        patchAdminUsersByIdAdminEmail,
        {
          path: { id },
          body: {
            data: { id, type: "user", attributes: { new_email: email } },
          },
        },
        options,
      );
    },

    /**
     * Confirm a user's email address (admin bypass).
     * @param id - User ID
     * @returns Updated user
     * @example
     * ```typescript
     * await admin.users.confirmEmail("user_123");
     * ```
     */
    confirmEmail: async (
      id: string,
      options?: RequestOptions,
    ): Promise<User> => {
      return rb.execute<User>(
        patchAdminUsersByIdConfirmEmail,
        { path: { id }, body: { data: { id, type: "user", attributes: {} } } },
        options,
      );
    },

    /**
     * Trigger a password reset email for a user (admin action).
     * @param id - User ID
     * @returns Updated user
     * @example
     * ```typescript
     * await admin.users.triggerPasswordReset("user_123");
     * ```
     */
    triggerPasswordReset: async (
      id: string,
      options?: RequestOptions,
    ): Promise<User> => {
      return rb.execute<User>(
        patchAdminUsersByIdResetPassword,
        { path: { id }, body: { data: { id, type: "user", attributes: {} } } },
        options,
      );
    },

    /**
     * Register a new user via invitation token — skips personal tenant creation,
     * accepts the invitation, and creates the appropriate AccessGrant.
     * @param email - Email address (must match the invitation)
     * @param password - Password (min 8 characters)
     * @param passwordConfirmation - Must match password
     * @param invitationToken - Raw invitation token from the invitation email
     * @param attrs - Optional extra attributes (first_name, last_name)
     * @returns Created user with JWT token
     * @example
     * ```typescript
     * const user = await admin.users.registerViaInvitation(
     *   "ada@example.com",
     *   "correct horse battery staple",
     *   "correct horse battery staple",
     *   "invitation-token",
     *   { first_name: "Ada", last_name: "Lovelace" },
     * );
     * ```
     */
    registerViaInvitation: async (
      email: string,
      password: string,
      passwordConfirmation: string,
      invitationToken: string,
      attrs?: { first_name?: string; last_name?: string },
      options?: RequestOptions,
    ): Promise<User> => {
      const { first_name, last_name } = attrs ?? {};

      return rb.execute<User>(
        postAdminUsersAuthRegisterViaInvitation,
        {
          body: {
            data: {
              type: "user",
              attributes: {
                email,
                password,
                password_confirmation: passwordConfirmation,
                invitation_token: invitationToken,
                first_name,
                last_name,
              },
            },
          },
        },
        options,
      );
    },

    /**
     * Delete a user and all associated data.
     * @param id - User ID
     * @param options - Optional request options.
     * @returns `true` when deletion succeeds.
     * @example
     * ```typescript
     * await admin.users.delete("user_123");
     * ```
     */
    delete: async (id: string, options?: RequestOptions): Promise<true> => {
      return rb.executeDelete(deleteAdminUsersById, { path: { id } }, options);
    },
  };
}
